import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import firebase from '../../../../firebase';
import TimesheetLogs from './pages/TimesheetLogs';
import TimesheetReview from './pages/TimesheetReview';
import TimesheetExport from './pages/TimesheetExport';
import Assignments from './pages/Assignments';
import { normalizeDepartment, ROLES } from './shared/constants';

const mapEngineers = raw =>
  Object.keys(raw || {}).map(id => {
    const u = raw[id] || {};
    return {
      id,
      name:
        u.name ||
        [u.firstName, u.lastName].filter(Boolean).join(' ') ||
        u.email ||
        id,
      email: u.email,
      department: normalizeDepartment(u.department) || '',
      // From css-timesheet seed: app_users/{app}/{uid}/assignedFmId
      assignedFmId: u.assignedFmId || null,
      role: ROLES.ENGINEER,
    };
  });

/** Timesheet engineers only — not every app member. */
const isTimesheetEngineer = eng =>
  !!(eng.assignedFmId || normalizeDepartment(eng.department));

/** True only when users/{uid}.fieldManager === true for this app. */
const isTimesheetFieldManager = (u, appname) => {
  if (!u || u.fieldManager !== true) return false;

  return (
    u.admin_appname === appname ||
    (u.admin_appnames &&
      Object.values(u.admin_appnames).indexOf(appname) > -1)
  );
};

const resolveTimesheetRole = (profile, appname, isSuperAdmin) =>
  isSuperAdmin ? ROLES.SYSTEM_ADMIN : ROLES.FIELD_MANAGER;

const fetchAppUsersMap = async appname => {
  // Prefer direct RTDB read — same source the provision script writes
  try {
    const snap = await firebase
      .database()
      .ref(`app_users/${appname}`)
      .once('value');
    if (snap.exists()) return snap.val() || {};
  } catch (err) {
    console.error('app_users RTDB read failed, falling back to API', err);
  }

  const authUser = firebase.auth().currentUser;
  if (!authUser) {
    throw new Error('Not authenticated');
  }
  const token = await authUser.getIdToken();
  const base = process.env.REACT_APP_APP_USERS;
  if (!base) {
    throw new Error('REACT_APP_APP_USERS is not configured');
  }
  const res = await fetch(
    `${base}/get-app-users-by-admin?appname=${encodeURIComponent(appname)}`,
    {
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': token,
      },
    },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || json.error || 'Failed to load users');
  }
  return json.data || {};
};

/**
 * Load FM profiles only for uids referenced by timesheet assignments.
 * Does NOT iterate parent app_admins (that list includes every sub-admin).
 */
const fetchFieldManagersByIds = async (appname, fmIds) => {
  const uniqueIds = [...new Set((fmIds || []).filter(Boolean))];
  const results = await Promise.all(
    uniqueIds.map(async id => {
      const snap = await firebase
        .database()
        .ref(`users/${id}`)
        .once('value');
      if (!snap.exists()) return null;
      const u = snap.val() || {};
      if (!isTimesheetFieldManager(u, appname)) return null;

      return {
        id,
        name:
          [u.firstName, u.lastName].filter(Boolean).join(' ') ||
          u.email ||
          id,
        email: u.email || '',
        role: ROLES.FIELD_MANAGER,
        department: '',
        assignedFmId: null,
      };
    }),
  );

  return results.filter(Boolean);
};

/**
 * Primary map from seed script: users/{fmUid}/assignments/{appname}/{engUid}
 */
const mergeAssignmentsFromFmNodes = async (
  appname,
  engineers,
  fieldManagers,
) => {
  const byId = {};
  engineers.forEach(eng => {
    byId[eng.id] = { ...eng };
  });

  await Promise.all(
    fieldManagers.map(async fm => {
      const snap = await firebase
        .database()
        .ref(`users/${fm.id}/assignments/${appname}`)
        .once('value');
      if (!snap.exists()) return;
      const assigned = snap.val() || {};
      Object.keys(assigned).forEach(engUid => {
        if (!assigned[engUid]) return;
        if (byId[engUid]) {
          byId[engUid].assignedFmId = fm.id;
        }
      });
    }),
  );

  return Object.keys(byId).map(id => byId[id]);
};

const Timesheet = props => {
  const { match, profile, isSuperAdmin } = props;
  const { appname } = match.params;
  const basePath = `/${appname}/ecommerce/timesheet`;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const role = useMemo(
    () => resolveTimesheetRole(profile || {}, appname, isSuperAdmin),
    [profile, appname, isSuperAdmin],
  );

  const currentUser = useMemo(() => {
    const authUid =
      (firebase.auth().currentUser && firebase.auth().currentUser.uid) || null;
    return {
      id: profile.uid || profile.user_id || authUid || 'platform-user',
      name:
        profile.name ||
        [profile.firstName, profile.lastName].filter(Boolean).join(' ') ||
        profile.email ||
        (role === ROLES.FIELD_MANAGER ? 'Field Manager' : 'Admin'),
      email: profile.email,
      role,
    };
  }, [profile, role]);

  const isSystemAdmin = isSuperAdmin;
  const isFieldManager = !isSuperAdmin;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        let raw = {};
        try {
          raw = await fetchAppUsersMap(appname);
        } catch (usersErr) {
          console.error(usersErr);
          raw = {};
        }

        let engineers = mapEngineers(raw).filter(isTimesheetEngineer);

        // FM uids come from engineer assignments only — never from app_admins
        const fmIdsFromEngineers = engineers
          .map(e => e.assignedFmId)
          .filter(Boolean);

        // If current user is an FM, include their assignment map even when empty
        const seedFmIds = isFieldManager
          ? [...fmIdsFromEngineers, currentUser.id]
          : fmIdsFromEngineers;

        let fieldManagers = [];
        try {
          fieldManagers = await fetchFieldManagersByIds(appname, seedFmIds);
        } catch (fmErr) {
          console.error(fmErr);
          fieldManagers = [];
        }

        try {
          engineers = await mergeAssignmentsFromFmNodes(
            appname,
            engineers,
            fieldManagers,
          );
          // Re-filter after merge in case assignment nodes added mapping
          engineers = engineers.filter(isTimesheetEngineer);
        } catch (assignErr) {
          console.error(assignErr);
        }

        // Refresh FM list if merge introduced new assignedFmIds
        const finalFmIds = [
          ...new Set(engineers.map(e => e.assignedFmId).filter(Boolean)),
        ];
        if (finalFmIds.some(id => !fieldManagers.find(fm => fm.id === id))) {
          try {
            fieldManagers = await fetchFieldManagersByIds(appname, finalFmIds);
          } catch (fmErr) {
            console.error(fmErr);
          }
        }

        if (!cancelled) {
          const adminUser = isSystemAdmin
            ? { ...currentUser, role: ROLES.SYSTEM_ADMIN }
            : null;
          setUsers(
            [
              ...engineers,
              ...fieldManagers,
              ...(adminUser ? [adminUser] : []),
            ].filter(
              (u, idx, arr) => arr.findIndex(x => x.id === u.id) === idx,
            ),
          );
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(err.message || 'Failed to load timesheets');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load().catch(err => {
      console.error(err);
      if (!cancelled) {
        setError(err.message || 'Failed to load timesheets');
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    appname,
    currentUser.id,
    currentUser.name,
    currentUser.email,
    isFieldManager,
    isSystemAdmin,
    isSuperAdmin,
    profile,
  ]);

  const scopeFmId = isSuperAdmin ? undefined : currentUser.id;

  if (loading) {
    return (
      <div
        style={{
          overflowX: 'hidden',
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 96px)',
          width: '100%',
          boxSizing: 'border-box',
          paddingTop: 24,
        }}
      >
        <Box p={4} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div
      style={{
        overflowX: 'hidden',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 96px)',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        paddingTop: 24,
        paddingBottom: 64,
      }}
    >
      {error && (
        <Box p={2} pb={0}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      <Switch>
        <Route
          exact
          path={`/:appname/ecommerce/timesheet`}
          render={() => (
            <TimesheetLogs
              currentUser={currentUser}
              users={users}
              basePath={basePath}
              appname={appname}
              fmId={scopeFmId}
            />
          )}
        />
        <Route
          exact
          path={`/:appname/ecommerce/timesheet/export`}
          render={() => (
            <TimesheetExport
              currentUser={currentUser}
              users={users}
              appname={appname}
              fmId={scopeFmId}
            />
          )}
        />
        <Route
          exact
          path={`/:appname/ecommerce/timesheet/assignments`}
          render={() =>
            isSuperAdmin ? (
              <Assignments users={users} />
            ) : (
              <Redirect to={basePath} />
            )
          }
        />
        <Route
          exact
          path={`/:appname/ecommerce/timesheet/:entryId`}
          render={routeProps => (
            <TimesheetReview
              {...routeProps}
              currentUser={currentUser}
              users={users}
              basePath={basePath}
              appname={appname}
            />
          )}
        />
        <Redirect to={basePath} />
      </Switch>
    </div>
  );
};

export default Timesheet;
